import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SaveShareModal = ({ isOpen, onClose, tripInfo, itinerary }) => {
  const [activeTab, setActiveTab] = useState('save');
  const [tripName, setTripName] = useState(tripInfo?.destination);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLink, setShareLink] = useState('https://taraletsgo.ph/itinerary/abc123xyz');
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = () => {
    // Save logic here
    alert(`Itinerary "${tripName}" saved successfully!`);
    onClose();
  };

  const handleExportPDF = () => {
    // PDF export logic here
    alert('Exporting itinerary as PDF...');
  };

  const handleShareEmail = () => {
    if (shareEmail) {
      alert(`Sharing itinerary with ${shareEmail}`);
      setShareEmail('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Save & Share Itinerary</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Save your trip plan and share with travel companions
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('save')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'save' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon name="Save" size={16} />
                Save
              </div>
            </button>
            <button
              onClick={() => setActiveTab('share')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'share' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon name="Share2" size={16} />
                Share
              </div>
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'export'
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon name="Download" size={16} />
                Export
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'save' && (
            <div className="space-y-5">
              <Input
                type="text"
                label="Trip Name"
                value={tripName}
                onChange={(e) => setTripName(e?.target?.value)}
                placeholder="Enter trip name"
                required
              />

              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Trip Summary</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Destination:</span>
                    <p className="font-medium text-foreground">{tripInfo?.destination}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration:</span>
                    <p className="font-medium text-foreground">{itinerary?.length} days</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dates:</span>
                    <p className="font-medium text-foreground">
                      {new Date(tripInfo.startDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      {' - '}
                      {new Date(tripInfo.endDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Travelers:</span>
                    <p className="font-medium text-foreground">{tripInfo?.travelers} people</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  iconName="Save"
                  onClick={handleSave}
                >
                  Save Itinerary
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'share' && (
            <div className="space-y-5">
              {/* Share Link */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Shareable Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 h-10 px-3 rounded-md border border-input bg-muted text-sm"
                  />
                  <Button
                    variant="outline"
                    size="default"
                    iconName={copied ? 'Check' : 'Copy'}
                    onClick={handleCopyLink}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Anyone with this link can view your itinerary
                </p>
              </div>

              {/* Share via Email */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Share via Email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e?.target?.value)}
                    placeholder="Enter email address"
                    className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button
                    variant="default"
                    size="default"
                    iconName="Send"
                    onClick={handleShareEmail}
                    disabled={!shareEmail}
                  >
                    Send
                  </Button>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  Share on Social Media
                </label>
                <div className="grid grid-cols-4 gap-3">
                  <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <Icon name="Facebook" size={20} className="text-white" />
                    </div>
                    <span className="text-xs text-foreground">Facebook</span>
                  </button>
                  <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all">
                    <div className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center">
                      <Icon name="Twitter" size={20} className="text-white" />
                    </div>
                    <span className="text-xs text-foreground">Twitter</span>
                  </button>
                  <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Icon name="MessageCircle" size={20} className="text-white" />
                    </div>
                    <span className="text-xs text-foreground">WhatsApp</span>
                  </button>
                  <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Icon name="Instagram" size={20} className="text-white" />
                    </div>
                    <span className="text-xs text-foreground">Instagram</span>
                  </button>
                </div>
              </div>

              {/* Collaborative Features */}
              <div className="bg-accent/10 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Users" size={20} className="text-accent mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Collaborative Planning</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Invite travel companions to edit and contribute to this itinerary
                    </p>
                    <Button variant="outline" size="sm" iconName="UserPlus">
                      Invite Collaborators
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'export' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleExportPDF}
                  className="flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                >
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Export as PDF</h4>
                    <p className="text-xs text-muted-foreground">Printable itinerary document</p>
                  </div>
                </button>

                <button className="flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="Calendar" size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Add to Calendar</h4>
                    <p className="text-xs text-muted-foreground">Google Calendar, iCal</p>
                  </div>
                </button>

                <button className="flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="Image" size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Export as Image</h4>
                    <p className="text-xs text-muted-foreground">PNG format for social media</p>
                  </div>
                </button>

                <button className="flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="Smartphone" size={24} className="text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Mobile App Sync</h4>
                    <p className="text-xs text-muted-foreground">Access offline on mobile</p>
                  </div>
                </button>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Export Tips</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• PDF exports include all activities, costs, and accommodation details</li>
                      <li>• Calendar exports create individual events for each activity</li>
                      <li>• Image exports are optimized for Instagram and Facebook stories</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveShareModal;